import { useCallback, useEffect, useState } from 'react';
import { getPoolOrder, type ApiClient, type IframePoolOrder } from '../api';

export interface RevealData {
  order: IframePoolOrder | null;
  loading: boolean;
  error: string | null;
  /** Call after mark_as_opened so the parent knows the order changed. */
  updateItem: (itemId: string, patch: Partial<IframePoolOrder['poolItems'][number]>) => void;
  /** Force-reload the order from the backend. Used to recover when
   *  `mark_as_opened` returns 422 because the item is already opened —
   *  the fresh payload carries `opened_at` + `best_active_offer`. */
  refetch: () => Promise<IframePoolOrder | null>;
}

/**
 * Loads a pool_order (incl. items + offer + videos) from the iframe
 * namespace. Idempotent: safe to remount with the same orderId.
 *
 * `updateItem` lets PackReveal push optimistic updates (opened_at) back
 * into the shared order state so the slider's "max reachable index"
 * logic sees fresh data without a refetch.
 */
export function useReveal(
  client: ApiClient,
  orderId: string | null,
): RevealData {
  const [order, setOrder] = useState<IframePoolOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (id: string, isCancelled?: () => boolean): Promise<IframePoolOrder | null> => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPoolOrder(client, id);
        if (isCancelled?.()) return null;
        // Sort opened items first so a partially‑revealed order resumes
        // at the right slide (matches web `sortBy(pool_items, opened_at)`).
        const sorted = [...data.poolItems].sort((a, b) => {
          const ao = a.openedAt ? 1 : 0;
          const bo = b.openedAt ? 1 : 0;
          return bo - ao;
        });
        const next = { ...data, poolItems: sorted };
        setOrder(next);
        return next;
      } catch (err) {
        if (isCancelled?.()) return null;
        setError(err instanceof Error ? err.message : 'Failed to load order');
        return null;
      } finally {
        if (!isCancelled?.()) setLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void load(orderId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [orderId, load]);

  const updateItem = useCallback(
    (itemId: string, patch: Partial<IframePoolOrder['poolItems'][number]>) => {
      setOrder((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          poolItems: prev.poolItems.map((i) =>
            String(i.id) === String(itemId) ? { ...i, ...patch } : i,
          ),
        };
      });
    },
    [],
  );

  const refetch = useCallback(async (): Promise<IframePoolOrder | null> => {
    if (!orderId) return null;
    return load(orderId);
  }, [orderId, load]);

  return { order, loading, error, updateItem, refetch };
}
